import Headroom from "headroom.js";

class MastheadParent {
	constructor(node, controller) {
		this.node = node;
		this.controller = controller;

		if (!this.node) {
			return;
		}

		// stores the timeout id of the enter hover intent
		this.enterTimeout = null;

		// stores the timeout of the leave hover intent
		this.leaveTimeout = null;

		// is the current item active
		this.active = false;

		// reference the direct child link in case we need to preventDefault on click
		this.link = this.node.querySelector(":scope > .masthead__link");

		// what types of pointer events are 'hoverable'
		this.hoverTypes = ["mouse", "pen"];

		// trigger the enter hover intent depending on the pointer type
		const onPointerEnter = (e) => {
			if (this.hoverTypes.includes(e.pointerType)) {
				if (typeof this.leaveTimeout === "number") {
					window.clearTimeout(this.leaveTimeout);
					this.leaveTimeout = null;
				}

				this.enterTimeout = setTimeout(
					() => this.activate(),
					this.controller.hoverDelay
				);
			}
		};

		// trigger the leave hover intent depending on the pointer type
		const onPointerLeave = (e) => {
			if (this.hoverTypes.includes(e.pointerType)) {
				if (typeof this.enterTimeout === "number") {
					window.clearTimeout(this.enterTimeout);
					this.enterTimeout = null;
				}

				this.leaveTimeout = window.setTimeout(
					() => this.deactivate(),
					this.controller.hoverDelay
				);
			}
		};

		// store the last pointer type on pointerup so we can potentially preventDefault onClick
		const onPointerUp = (e) => {
			this.pointerType = e.pointerType ?? null;
		};

		// if not a hoverable type, simply toggle instead of following the link
		const onClick = (e) => {
			if (!this.hoverTypes.includes(this.pointerType)) {
				e.preventDefault();
				this.toggle();
			}

			if (this.pointerType) {
				delete this.pointerType;
			}
		};

		// when a element outside of this node is focused, close this menu
		const onFocus = () => {
			let target = document.activeElement;

			while (target && target.parentNode) {
				if (target === this.node) {
					return;
				}

				target = target.parentNode;
			}

			this.deactivate();
		};

		this.node.addEventListener("pointerenter", onPointerEnter);
		this.node.addEventListener("pointerleave", onPointerLeave);
		this.node.addEventListener("pointerup", onPointerUp);
		document.body.addEventListener("focus", onFocus, true);

		if (this.link) {
			this.link.addEventListener("click", onClick);
			// Keyboard support: Enter on parent link activates mega menu and focuses first mega link
			this.link.addEventListener("keydown", (e) => {
				if (e.key === "Enter") {
					e.preventDefault();
					this.activate(true); // Pass a flag for keyboard activation
				}
			});
		}

		// Mega menu tab navigation: tabbing out of last mega link deactivates mega menu and focuses next top-level item
		if (this.node.classList.contains("masthead__item--mega")) {
			const megaLinks = Array.from(
				this.node.querySelectorAll(
					".masthead__mega-menu a, .masthead__mega-menu button"
				)
			);
			megaLinks.forEach((link, idx) => {
				link.addEventListener("keydown", (e) => {
					if (
						e.key === "Tab" &&
						!e.shiftKey &&
						idx === megaLinks.length - 1
					) {
						// Last link, tabbing out
						this.deactivate();
						// Focus next top-level item
						const topItems = Array.from(
							this.controller.node.querySelectorAll(
								".masthead__item--depth-0 > .masthead__link"
							)
						);
						const myIdx = topItems.indexOf(this.link);
						if (myIdx !== -1 && topItems[myIdx + 1]) {
							topItems[myIdx + 1].focus();
						}
					}
				});
			});
		}
	}

	/**
	 * Toggle this dropdown menu state
	 */
	toggle() {
		this.active ? this.deactivate() : this.activate();
	}

	/**
	 * Activate this dropdown menu state
	 */
	activate(keyboard = false) {
		if (!this.active) {
			this.active = true;
			this.controller.deactivate(this);

			if (this.node.classList.contains("masthead__item--mega")) {
				this.controller.node.classList.add(
					this.controller.megaActiveClass
				);
				if (keyboard) {
					// Focus the first link inside the mega menu
					const firstMegaLink = this.node.querySelector(
						".masthead__mega-menu a, .masthead__mega-menu button"
					);
					if (firstMegaLink) {
						firstMegaLink.focus();
					}
				}
			}

			this.node.classList.add(this.controller.parentActiveClass);
			this.node.dispatchEvent(new Event("masthead:activate"));
		}
	}

	/**
	 * Deactivate this dropdown menu state
	 */
	deactivate() {
		if (this.active) {
			this.active = false;
			setTimeout(() => {
				this.node.classList.remove(this.controller.parentActiveClass);

				this.node.dispatchEvent(new Event("masthead:deactivate"));
			}, 150); // Delay to allow overlap
		}
	}
}

class Masthead {
	constructor() {
		this.node = document.querySelector(".masthead");

		if (!this.node) {
			return;
		}

		// the delay used for hover-intent
		this.hoverDelay = 200;

		//primary element
		this.primary = this.node.querySelector(".masthead__primary");

		// the item active class
		this.parentActiveClass = "masthead__item--active";
		this.megaActiveClass = "masthead--active-mega";

		// node references for interaction
		this.nav = this.node.querySelector(".masthead__menu");

		// initialize menu items that are parents
		this.parents = Array.from(
			this.node.querySelectorAll(
				".masthead__item--depth-0.menu-item-has-children, .masthead__item--depth-0.masthead__item--mega"
			)
		).map((node) => {
			return new MastheadParent(node, this);
		});

		if (this.primary && this.node) {
			this.primary.addEventListener("mouseleave", () => {
				this.node.classList.remove(this.megaActiveClass);
			});

			this.primary.addEventListener("focusout", (e) => {
				// Only remove if focus moves outside masthead__primary
				if (!this.primary.contains(e.relatedTarget)) {
					this.node.classList.remove(this.megaActiveClass);
				}
			});
		}

		// headroom sticky hide configuration - @see https://www.npmjs.com/package/headroom.js
		this.headroom = new Headroom(this.node, {
			tolerance: {
				up: 7,
				down: 5,
			},
			classes: {
				initial: "masthead",
				pinned: "masthead--pinned",
				unpinned: "masthead--unpinned",
				top: "masthead--top",
				notTop: "masthead--not-top",
				bottom: "masthead--bottom",
				notBottom: "masthead--not-bottom",
				frozen: "masthead--frozen",
			},
		});
		this.headroom.init();
		this.headroom.update(this.headroom);

		// deactivate the mode of the menu on escape key
		const onKeyUp = (e) => {
			switch (e.key) {
				case "Escape":
					if (this.mode) {
						this.setMode("");
					}
					break;
			}
		};

		// deactivate mode when clicking outside of the masthead
		const onClick = (e) => {
			let target = e.target;

			while (target.parentNode) {
				if (target === this.nav) {
					return;
				}

				target = target.parentNode;
			}

			this.deactivate();
		};

		this.node.addEventListener("keyup", onKeyUp);

		document.body.addEventListener("click", onClick);
	}

	/**
	 * Deactivate all dropdown menus (optionally exclude one menu)
	 *
	 * @param {MastheadParent} exclude
	 */
	deactivate(exclude) {
		this.parents.forEach((parent) => {
			if (!exclude || parent !== exclude) {
				parent.deactivate();
			}
		});
	}
}

document.addEventListener("DOMContentLoaded", () => {
	new Masthead();

	// ── Legacy login-toggle wiring (kept for backward compat) ──
	document
		.querySelectorAll('[data-action="login-toggle"]')
		.forEach(function (btn) {
			btn.addEventListener("click", function (e) {
				e.preventDefault();
				var form = btn.nextElementSibling;
				if (form) {
					form.style.display =
						form.style.display === "none" ? "block" : "none";
				}
			});
		});
	document
		.querySelectorAll('[data-action="login-close"]')
		.forEach(function (btn) {
			btn.addEventListener("click", function (e) {
				e.preventDefault();
				var form = btn.closest(".login-form");
				if (form) {
					form.style.display = "none";
				}
			});
		});

	// ── Member dropdown toggle ──
	(function initMemberDropdown() {
		const memberDropdown = document.querySelector(".masthead__member-dropdown");
		if (!memberDropdown) return;

		const toggle = memberDropdown.querySelector(
			".masthead__member-dropdown-toggle"
		);
		const panel = memberDropdown.querySelector(".masthead__member-panel");
		if (!toggle || !panel) return;

		function openPanel() {
			toggle.setAttribute("aria-expanded", "true");
			panel.setAttribute("aria-hidden", "false");
		}

		function closePanel() {
			toggle.setAttribute("aria-expanded", "false");
			panel.setAttribute("aria-hidden", "true");
		}

		toggle.addEventListener("click", function (e) {
			e.preventDefault();
			e.stopPropagation();
			e.stopImmediatePropagation();
			if (panel.getAttribute("aria-hidden") === "false") {
				closePanel();
			} else {
				openPanel();
			}
		});

		// Mobile menu "Log In" button opens the same dropdown panel
		document
			.querySelectorAll('[data-action="mobile-login-open"]')
			.forEach(function (btn) {
				btn.addEventListener("click", function () {
					var mobileMenu = document.getElementById("mobile-menu");
					var menuToggle = document.querySelector(".mobile-menu-toggle");
					if (mobileMenu) {
						mobileMenu.setAttribute("aria-hidden", "true");
					}
					if (menuToggle) {
						menuToggle.setAttribute("aria-expanded", "false");
					}
					openPanel();
				});
			});

		document.addEventListener("click", function (e) {
			if (!memberDropdown.contains(e.target)) {
				closePanel();
			}
		});

		document.addEventListener("keydown", function (e) {
			if (e.key === "Escape") {
				closePanel();
			}
		});

		// ── Passwordless login AJAX (only present for logged-out visitors) ──
		const emailForm = document.getElementById("nam-login-email-form");
		const codeForm = document.getElementById("nam-login-code-form");
		if (!emailForm || !codeForm || typeof namLogin === "undefined") return;

		const emailInput = document.getElementById("nam-login-email");
		const codeInput = document.getElementById("nam-login-code");
		const codeEmailHidden = document.getElementById("nam-login-code-email");
		const backBtn = codeForm.querySelector(".masthead__login-back");

		function setMsg(form, text, type) {
			const el = form.querySelector(".masthead__login-msg");
			if (!el) return;
			el.textContent = text;
			el.className =
				"masthead__login-msg" +
				(type ? " masthead__login-msg--" + type : "");
		}

		function setLoading(form, loading) {
			const btn = form.querySelector(".masthead__login-submit");
			if (btn) btn.disabled = loading;
		}

		emailForm.addEventListener("submit", function (e) {
			e.preventDefault();
			const email = (emailInput.value || "").trim();
			if (!email) return;

			setMsg(emailForm, "", "");
			setLoading(emailForm, true);

			const body = new FormData();
			body.append("action", "nam_login_send_code");
			body.append("nonce", namLogin.nonce);
			body.append("email", email);

			fetch(namLogin.ajaxurl, { method: "POST", body: body })
				.then(function (r) {
					return r.json();
				})
				.then(function (res) {
					setLoading(emailForm, false);
					if (res.success && res.data && res.data.admin_redirect) {
						window.location.href = res.data.admin_redirect;
						return;
					}
					if (res.success) {
						setMsg(emailForm, "", "");
						codeEmailHidden.value = email;
						emailForm.style.display = "none";
						codeForm.style.display = "block";
						if (codeInput) codeInput.focus();
					} else {
						setMsg(
							emailForm,
							(res.data && res.data.message) || "Something went wrong.",
							"error"
						);
					}
				})
				.catch(function () {
					setLoading(emailForm, false);
					setMsg(emailForm, "Network error. Please try again.", "error");
				});
		});

		codeForm.addEventListener("submit", function (e) {
			e.preventDefault();
			const code = (codeInput.value || "").trim();
			const email = codeEmailHidden.value;
			if (!code || !email) return;

			setMsg(codeForm, "", "");
			setLoading(codeForm, true);

			const body = new FormData();
			body.append("action", "nam_login_verify_code");
			body.append("nonce", namLogin.nonce);
			body.append("email", email);
			body.append("code", code);

			fetch(namLogin.ajaxurl, { method: "POST", body: body })
				.then(function (r) {
					return r.json();
				})
				.then(function (res) {
					setLoading(codeForm, false);
					if (res.success && res.data && res.data.redirect) {
						setMsg(codeForm, "Success! Redirecting\u2026", "success");
						window.location.href = res.data.redirect;
					} else {
						setMsg(
							codeForm,
							(res.data && res.data.message) || "Verification failed.",
							"error"
						);
					}
				})
				.catch(function () {
					setLoading(codeForm, false);
					setMsg(codeForm, "Network error. Please try again.", "error");
				});
		});

		if (backBtn) {
			backBtn.addEventListener("click", function () {
				codeForm.style.display = "none";
				emailForm.style.display = "block";
				setMsg(codeForm, "", "");
				if (codeInput) codeInput.value = "";
			});
		}
	})();
});
